import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { culture } from '../../../../Helpers/dates';
import useTranslate from '../../../../Hooks/useTranslate';
import { CenteredFlexBox } from '../../../FlexBox';
import StatTile from '../../charts/StatTile';
import MiniLineChart from '../../charts/MiniLineChart';
import ReportCard from '../../charts/ReportCard';
import NoData from './NoData';
import DetailList from './DetailList';
import { rate } from './reportUtils';

const fmtPct = (v) => `${(v || 0).toFixed(2)}%`;

/**
 * MobileRollingReport — rolling redemption (getRollingRedemption): the daily
 * "Average" series over the full window (desktop: totalPractices /
 * totalAnsweredNum * 100, date-ordered) drawn as a line chart with a dashed
 * mean reference line, plus a tap-a-point details panel (that day's redemption,
 * practices, answered). See MOBILE_PLAN.md §F(9).
 */
const MobileRollingReport = function MobileRollingReport({ rolling = null }) {
  const theme = useTheme();
  const translate = useTranslate();
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => setSelectedIndex(null), [rolling]);

  const series = useMemo(() => {
    const seen = new Set();
    return (rolling || [])
      .filter((p) => p?.rollingDate)
      .sort((a, b) => new Date(a.rollingDate) - new Date(b.rollingDate))
      .filter((p) => {
        if (seen.has(p.rollingDate)) return false;
        seen.add(p.rollingDate);
        return true;
      })
      .map((p) => ({
        label: new Date(p.rollingDate).toLocaleDateString(culture, {
          day: '2-digit',
          month: '2-digit',
        }),
        fullLabel: new Date(p.rollingDate).toLocaleDateString(culture, {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }),
        value:
          p.totalPractices && p.totalAnsweredNum
            ? rate(p.totalPractices, p.totalAnsweredNum)
            : 0,
        practices: p.totalPractices || 0,
        answered: p.totalAnsweredNum || 0,
      }));
  }, [rolling]);

  const latest = series.length ? series[series.length - 1].value : 0;
  const mean = series.length
    ? series.reduce((acc, p) => acc + p.value, 0) / series.length
    : 0;
  const selected = selectedIndex != null ? series[selectedIndex] : null;

  return (
    <>
      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('REDEMPTION')}
          value={fmtPct(latest)}
          valueColor="primary.main"
        />
        <StatTile label={translate('AVERAGE')} value={fmtPct(mean)} />
      </CenteredFlexBox>
      <ReportCard title={translate('ROLLING_REDEMPTION')}>
        {series.length ? (
          <MiniLineChart
            points={series}
            color={theme.palette.primary.main}
            averageValue={mean}
            onSelect={setSelectedIndex}
            selectedIndex={selectedIndex}
          />
        ) : (
          <NoData />
        )}
      </ReportCard>

      {selected ? (
        <ReportCard title={selected.fullLabel}>
          <DetailList
            items={[
              {
                caption: translate('REDEMPTION'),
                value: fmtPct(selected.value),
              },
              {
                caption: translate('ANSWERED_CALLS'),
                value: selected.answered.toLocaleString(),
              },
              {
                caption: translate('PRACTICES'),
                value: selected.practices.toLocaleString(),
              },
            ]}
          />
        </ReportCard>
      ) : null}
    </>
  );
};

export default MobileRollingReport;
